import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Container, Navbar, Nav } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import styled from '@emotion/styled';

import CONST from '../constants';
import { ReactComponent as Logo } from '../resources/logo.svg';
import { VscClose, VscMenu } from 'react-icons/vsc';
import { MdPhoneInTalk as PhoneIcon } from 'react-icons/md';

const StyledLogoLink = styled(NavLink)`
    text-decoration: none;
`;

const StyledLogo = styled(Logo)`
    height: 8vw;
    color: ${CONST.COLOR.TEXT};
    vertical-align: middle;
    transition: all 300ms ease;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        height: 2.5vw;
    }

    &:hover {
        color: ${CONST.COLOR.THEME};

        & .icon {
            color: ${CONST.COLOR.LOGO.NUMBER_DARKER_30};
        }
    }

    & .icon {
        color: ${CONST.COLOR.LOGO.NUMBER};
        transition: all 300ms ease;
    }
`;

const StyledLogoText = styled.div`
    display: inline-block;
    margin-left: 2vw;
    font-weight: bold;
    transition: all 300ms ease;
    vertical-align: middle;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        margin-left: 0.75vw;
    }

    & > p {
        display: block;
        margin: 0;
        color: ${CONST.COLOR.THEME};
        font-size: 3.5vw;
        line-height: 1.25;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            font-size: 1vw;
            line-height: 1.1;
        }
    }
    & > p:first-of-type {
        font-size: 5vw;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            font-size: 1.5vw;
        }
    }

    & > p > span {
        color: ${CONST.COLOR.THEME_SECONDARY};
    }
`;

const StyledNavbar = styled(Navbar)`
    width: 100%;
    height: ${CONST.HEADER_HEIGHT.MOBILE};
    padding-left: 2vw;
    padding-right: 3vw;
    background: ${CONST.COLOR.NAV};
    box-shadow: 0 4px 6px -6px #000;
    transition: all 400ms ease;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        height: ${CONST.HEADER_HEIGHT.STATIC};
        padding-right: 3vw;
    }

    &.scroll-enter-active {
        background: none;
        transition: background 600ms ease;
    }
    &.scroll-enter-done {
        background: none;
    }
    &.scroll-exit {
        background: none;
    }
    &.scroll-exit-active {
        background: ${CONST.COLOR.BG};
        transition: background 600ms ease;
    }
`;

const StyledNavLink = styled(NavLink)`
    margin-right: 2.5vw;
    padding: 5px 2px;
    color: ${CONST.COLOR.TEXT};
    font-size: 1.1vw;
    text-decoration: none;
    border-top: 3px solid rgba(0, 0, 0, 0);
    border-bottom: 3px solid rgba(0, 0, 0, 0);
    transition: all 300ms ease;

    &:hover {
        color: ${CONST.COLOR.THEME};
    }

    &.active {
        color: ${CONST.COLOR.THEME};
        border-bottom-color: ${CONST.COLOR.THEME};
    }
`;

const PhoneLink = styled.a`
    position: absolute;
    left: 50%;
    color: ${CONST.COLOR.PHONE};
    font-size: 1.25vw;
    font-weight: bold;
    text-decoration: none;
    transform: translateX(-50%);
    transition: all 300ms ease;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        position: relative;
        left: initial;
        transform: initial;
        margin-left: 1vw;

        & > svg {
            margin-right: 0.2vw;
        }

        & > p {
            display: inline-block;
            margin: 0;
            vertical-align: middle;
            letter-spacing: -0.05vw;
        }
    }

    &:hover {
        color: ${CONST.COLOR.THEME_DARKER_30};
    }
`;

const Overlay = styled(Container)`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    height: 100vh;
    background: ${CONST.COLOR.BG};
    color: ${CONST.COLOR.TEXT};
    z-index: 10;

    &.animation-enter {
        transform: translateX(100%);
    }
    &.animation-enter-active {
        transform: translateX(0);
        transition: transform 400ms ease;
    }
    &.animation-exit {
        transform: translateX(0);
    }
    &.animation-exit-active {
        transform: translateX(100%);
        transition: transform 400ms ease;
    }
`;

const OverlayBody = styled(Container)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

const OverlayNavLink = styled(NavLink)`
    display: block;
    color: inherit;
    font-size: 8vw;
    text-align: center;
    text-decoration: none;

    &:not(:last-of-type) {
        margin-bottom: 20px;
    }

    &.active {
        color: ${CONST.COLOR.THEME};
    }

    &.active::before {
        position: absolute;
        content: '•';
        transform: translate(-125%, -10%);
    }
`;

const Header = () => {
    /**************************************************************
     * HOOKS
     *************************************************************/

    const location = useLocation();
    const [isNavClicked, setIsNavClicked] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        setIsNavClicked(false);
    }, [location]);

    useLayoutEffect(() => {
        function handleResize() {
            setIsMobile(window.innerWidth <= CONST.SCREEN.BREAKPOINT.SM);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**************************************************************
     * FRAGMENTS
     *************************************************************/

    const fragment = {};

    // Mobile-first
    if (isMobile) {
        fragment.links = isNavClicked ? <VscClose color={CONST.COLOR.TEXT} size="8vw" onClick={() => setIsNavClicked(!isNavClicked)} /> : <VscMenu color={CONST.COLOR.TEXT} size="8vw" onClick={() => setIsNavClicked(!isNavClicked)} />;
    } else {
        fragment.links = (
            <React.Fragment>
                <StyledNavLink to="overview">사업개요</StyledNavLink>
                <StyledNavLink to="analysis">입지분석</StyledNavLink>
                <StyledNavLink to="premium">프리미엄</StyledNavLink>
                <StyledNavLink to="floor-plan">층별안내</StyledNavLink>
                <PhoneLink href="tel:010-5153-6588">
                    <PhoneIcon size="1.9vw" />
                    <p>{CONST.PHONE}</p>
                </PhoneLink>
            </React.Fragment>
        );

        if (isNavClicked) {
            setIsNavClicked(false);
        }
    }

    /**************************************************************
     * CORE
     *************************************************************/

    return (
        <React.Fragment>
            <CSSTransition in={isNavClicked} timeout={400} classNames="animation" onEnter={() => (document.body.style.overflowY = 'hidden')} onExit={() => (document.body.style.overflowY = 'visible')} unmountOnExit>
                <Overlay fluid className="p-0">
                    <OverlayBody fluid className="p-0">
                        <OverlayNavLink to="overview">사업개요</OverlayNavLink>
                        <OverlayNavLink to="analysis">입지분석</OverlayNavLink>
                        <OverlayNavLink to="premium">프리미엄</OverlayNavLink>
                        <OverlayNavLink to="floor-plan">층별안내</OverlayNavLink>
                    </OverlayBody>
                </Overlay>
            </CSSTransition>
            <StyledNavbar variant="dark" sticky="top">
                <Container fluid className="p-0 m-0">
                    <Navbar.Brand>
                        <StyledLogoLink to="/">
                            <StyledLogo />
                            <StyledLogoText>
                                <p>HIGHCUBE</p>
                                <p>
                                    하이큐브 <span>구로</span>
                                </p>
                            </StyledLogoText>
                        </StyledLogoLink>
                    </Navbar.Brand>
                    <Nav className="align-items-center">{fragment.links}</Nav>
                    {isMobile && (
                        <PhoneLink href={`tel:${CONST.PHONE}`}>
                            <PhoneIcon size="8vw" />
                        </PhoneLink>
                    )}
                </Container>
            </StyledNavbar>
        </React.Fragment>
    );
};

export default Header;
